import type { PaymentMethodId, ValidationOptions } from '@subscriptions/domain/types';
import type {
  PaymentProvider,
  PaymentMethodType,
  CardBrand,
  PaymentMethodStatus,
} from '@subscriptions/domain/value-objects';
import {
  createPaymentProvider,
  createPaymentMethodType,
  createCardBrand,
  createPaymentMethodStatus,
} from '@subscriptions/domain/value-objects';
import { createValueResolver } from '@subscriptions/domain/utils';

/**
 * Payment method domain entity.
 *
 * Immutable by design — represents a registered payment method (card or SPEI).
 * Only created/reconstructed via factory function.
 */
export type PaymentMethod = Readonly<{
  uuid: PaymentMethodId;
  provider: PaymentProvider;
  providerId: string | null;
  providerPublicId: string;
  methodType: PaymentMethodType;
  orgUuid: string;
  brand: CardBrand;
  expirationDate: string | null;
  lastFourDigits: string | null;
  status: PaymentMethodStatus;
  /**
   * Reactivation CLABE validity, present only on a churned block with an open SPEI
   * payment quoted today. The amount it holds for is the block's own total.
   */
  reactivation?: { quotedOn: string; clabeValidUntil: string };
}>;

/**
 * Creates a validated PaymentMethod entity.
 *
 * @param params - Raw primitives from infrastructure layer
 * @param options - Validation options; with `softValidation` invalid fields degrade to empty fallbacks
 * @returns Frozen PaymentMethod entity
 * @throws Error if any required field is invalid (strict mode only)
 */
export const createPaymentMethod = (
  params: {
    uuid: string;
    provider: string;
    providerId: string | null;
    providerPublicId: string;
    methodType: string;
    orgUuid: string;
    brand: string | null;
    expirationDate: string | null;
    lastFourDigits: string | null;
    status: string;
    reactivation?: { quotedOn: string; clabeValidUntil: string };
  },
  options?: ValidationOptions,
): PaymentMethod => {
  const resolve = createValueResolver(options);

  if (!options?.softValidation && !params.uuid?.trim()) {
    throw new Error('Payment method UUID is required');
  }

  return Object.freeze({
    uuid: params.uuid as PaymentMethodId,
    provider: resolve<PaymentProvider>(() => createPaymentProvider(params.provider), ''),
    providerId: params.providerId,
    providerPublicId: params.providerPublicId,
    methodType: resolve<PaymentMethodType>(() => createPaymentMethodType(params.methodType), ''),
    orgUuid: params.orgUuid,
    brand: resolve<CardBrand>(() => createCardBrand(params.brand), null),
    expirationDate: params.expirationDate,
    lastFourDigits: params.lastFourDigits,
    status: resolve<PaymentMethodStatus>(() => createPaymentMethodStatus(params.status), ''),
    reactivation: params.reactivation,
  });
};
